import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { COMPANY_INFO, VINDUER_PRICES } from "../data/siteData";
import ReviewSlider from "../components/ReviewSlider";
import CmsGallery from "../components/CmsGallery";
import ContactForm from "../components/ContactForm";

export default function CityPage({ cityData }) {
  if (!cityData) return null;

  const benefits = [
    {
      icon: "fa-location-dot",
      title: `Lokalt i ${cityData.cityName}`,
      desc: "Vi kender området og er hurtigt fremme"
    },
    {
      icon: "fa-bolt",
      title: "Hurtig tid",
      desc: "Fleksible aftaler der passer til dig"
    },
    {
      icon: "fa-tag",
      title: "Skarpe priser",
      desc: "Gennemsigtig prisfastsættelse uden skjulte gebyrer"
    },
    {
      icon: "fa-circle-check",
      title: "Professionelt resultat",
      desc: "Kvalitet og grundighed – hver gang"
    }
  ];

  return (
    <>
      <Helmet>
        <title>{cityData.title}</title>
        <meta name="description" content={cityData.metaDescription} />
        <meta property="og:title" content={cityData.title} />
        <meta property="og:description" content={cityData.metaDescription} />
        <meta
          property="og:image"
          content={
            cityData.heroImage ||
            "https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/gemini-generated-image-3jxk3t3jxk3t3jxk-e1763482441514.png"
          }
        />
        <link rel="canonical" href={`https://nf-polering.dk/${cityData.slug}`} />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[620px] flex items-center bg-slate-950 text-white overflow-hidden py-12 sm:py-16 lg:py-20 border-b border-slate-800">
        {/* Background photo & overlay */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/img-1811_f7b7c065.jpg"
            alt={`vinduespudser i ${cityData.cityName.toLowerCase()}`}
            className="w-full h-full object-cover object-center animate-hero-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/55 to-slate-950/20" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Col: Info */}
            <div className="lg:col-span-6 space-y-6 text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
                {cityData.h1}
              </h1>

              <p className="text-base sm:text-lg text-slate-200 leading-relaxed">
                {cityData.heroSubtitle ||
                  "Få hurtig tid, skarpe priser og et resultat der holder. Ring nu eller udfyld formularen – så får du din pris med det samme."}
              </p>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-4 pt-1">
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="inline-flex items-center justify-center gap-3 px-7 py-3.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white font-bold text-base transition-all transform active:scale-95 whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring og få en pris</span>
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="pt-6 border-t border-white/20 grid grid-cols-3 gap-4 sm:gap-6 text-left max-w-lg">
                <div>
                  <div className="text-xl sm:text-2xl font-extrabold text-cyan-400">Fra 99,-</div>
                  <div className="text-xs text-slate-300">Fast lav pris</div>
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-extrabold text-cyan-400">100%</div>
                  <div className="text-xs text-slate-300">Garanti</div>
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-extrabold text-cyan-400">4-12 uger</div>
                  <div className="text-xs text-slate-300">Abonnement</div>
                </div>
              </div>
            </div>

            {/* Right Col: Webform */}
            <div className="lg:col-span-6">
              <ContactForm
                id="tilbud"
                title="Udfyld formularen og få et tilbud"
                subtitle={`Vi svarer hurtigt med en skarp pris på vinduespudsning i ${cityData.cityName}.`}
                defaultCity={cityData.cityName}
                defaultPostnr={cityData.cityName}
                defaultService="Vinduespudsning"
                variant="hero"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {benefits.map((benefit, i) => (
              <div
                key={i}
                className="text-left flex flex-col items-start justify-start"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="text-2xl sm:text-3xl text-cyan-600 shrink-0">
                    <i className={`fa-solid ${benefit.icon}`} aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg">
                    {benefit.title}
                  </h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Table for the City */}
      <section className="py-16 sm:py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Traditionel Vinduespudsning i {cityData.cityName}
            </h2>
            <p className="mt-3 text-slate-600 text-base">
              Gennemsigtige priser – så du ved præcis hvad det koster
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {VINDUER_PRICES.map((priceCard, idx) => (
              <div
                key={idx}
                className="relative rounded-none p-7 sm:p-8 flex flex-col justify-between transition-all bg-white border border-slate-200 shadow-sm"
              >
                <div>
                  <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-4">
                    {priceCard.title}
                  </h3>

                  <div className="flex items-baseline gap-1.5 mb-4 pb-4 border-b border-slate-100">
                    <span className="text-3xl sm:text-4xl font-black text-slate-900">
                      {priceCard.price}
                    </span>
                    <span className="text-sm font-semibold text-slate-500">
                      {priceCard.unit}
                    </span>
                  </div>

                  <p className="text-xs sm:text-sm text-slate-600 mb-6 italic">
                    {priceCard.note}
                  </p>

                  <ul className="space-y-3 mb-8 text-sm text-slate-700">
                    {priceCard.features.map((feat, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2.5">
                        <i className="fa-solid fa-check text-cyan-600 text-sm mt-0.5 shrink-0" aria-hidden="true" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <a
                    href={COMPANY_INFO.phoneTel}
                    className="w-full py-3.5 px-4 rounded-full font-bold text-sm text-center transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 transform active:scale-95 flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    <span>FÅ TILBUD</span>
                    <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-500 mt-8 max-w-2xl mx-auto">
            Alle priser er fra-priser og gælder almindelige et-plans huse. Særlige adgangsforhold, meget snavsede ruder eller ekstra arbejde kan medføre tillæg.
          </p>
        </div>
      </section>

      {/* Verbatim Sections */}
      <section className="py-16 sm:py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24 sm:space-y-32">
          {cityData.textSections &&
            cityData.textSections.map((sec, sIdx) => (
              <div
                key={sIdx}
                className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center"
              >
                <div
                  className={`lg:col-span-7 space-y-5 text-slate-700 leading-relaxed text-base ${
                    sIdx % 2 === 0 ? "lg:order-2" : "lg:order-1"
                  }`}
                >
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                    {sec.h2}
                  </h2>
                  {sec.paragraphs.map((p, pIdx) => (
                    <p key={pIdx}>{p}</p>
                  ))}

                  {sec.bulletList && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-slate-900 font-semibold text-sm">
                      {sec.bulletList.map((item, bIdx) => (
                        <li key={bIdx} className="flex items-center gap-2.5">
                          <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {sec.closingText && (
                    <p className="pt-2 font-medium text-slate-900">{sec.closingText}</p>
                  )}

                  <div className="pt-3">
                    <a
                      href="tel:30146070"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 transform active:scale-95"
                    >
                      <span>Få et tilbud</span>
                      <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                    </a>
                  </div>
                </div>

                <div
                  className={`lg:col-span-5 ${
                    sIdx % 2 === 0 ? "lg:order-1" : "lg:order-2"
                  }`}
                >
                  <div className="rounded-none overflow-hidden border border-slate-200 shadow-sm bg-white">
                    <img
                      src={
                        sIdx === 0
                          ? cityData.byskiltImage || sec.image || "https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/file.png"
                          : sIdx === 1
                          ? "https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/img-1811.jpg"
                          : "https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/fullsizerender.jpeg"
                      }
                      alt={
                        sIdx === 0
                          ? sec.imageAlt || `Professionel vinduespudser ved ${cityData.cityName} byskilt – NF Polering`
                          : sIdx === 1
                          ? `Kvalitetsbevidst vinduespolering i ${cityData.cityName} og omegn – NF Polering`
                          : `Professionel vinduespolering i ${cityData.cityName} – NF Polering`
                      }
                      className="w-full h-auto object-contain block"
                    />
                  </div>
                </div>
              </div>
            ))}
        </div>
      </section>

      {/* Dynamic Gallery */}
      <CmsGallery
        slug="vinduespudsning"
        title={`Billeder fra vores vinduespudsning i ${cityData.cityName}`}
        subtitle="Se resultaterne fra vores professionelle vinduespolering på Vestsjælland."
      />

      {/* Reviews */}
      <ReviewSlider />

      {/* Bottom Contact Section */}
      <section className="py-16 sm:py-24 bg-slate-900 text-white border-t border-slate-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
              Book din vinduespudsning i {cityData.cityName} her
            </h2>
            <p className="mt-3 text-slate-300 text-sm sm:text-base">
              Udfyld formularen eller kontakt os direkte. Vi svarer hurtigt med en fast og skarp pris.
            </p>
          </div>
          <ContactForm
            id="kontakt"
            title="Udfyld formularen og få et tilbud"
            subtitle={`Vi dækker ${cityData.cityName} og hele lokalområdet.`}
            defaultCity={cityData.cityName}
            defaultPostnr={cityData.cityName}
            defaultService="Vinduespudsning"
          />
        </div>
      </section>
    </>
  );
}
